/**
 * ProfileScreen — ficha estilo Pokémon: avatar + datos + insignias sprite.
 */
import { useEffect, useState } from 'react';
import { useApp } from '@/state/store';
import { visitsApi, doorApi } from '@/lib/api';
import {
  UserSprite, HeartSprite, CoinSprite, DoorSprite, BellSprite,
  KeySprite, MailSprite, QRSprite,
} from '@/components/Sprites';

export function ProfileScreen() {
  const { user, community, goto } = useApp();
  const [doorsOpened, setDoorsOpened] = useState<number | null>(null);
  const [bellRings, setBellRings] = useState<number | null>(null);

  useEffect(() => {
    const onB = () => goto('home');
    window.addEventListener('vicin-gb:b', onB);
    return () => window.removeEventListener('vicin-gb:b', onB);
  }, [goto]);

  // Fetch real stats from history endpoints. Best-effort — show "—" on error.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await visitsApi.history();
        if (!cancelled) setBellRings(r.rings?.length ?? 0);
      } catch { if (!cancelled) setBellRings(0); }
      try {
        const r = await doorApi.status();
        // /door/status doesn't expose a count; leave null unless backend
        // grows one. We use ring history as the proxy for activity.
        if (!cancelled && r?.configured) setDoorsOpened(null);
      } catch { /* leave null */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const roleIcon = (() => {
    switch (user?.role) {
      case 'admin': return '★';
      case 'owner': return '◆';
      case 'resident': return '◇';
      case 'member': return '○';
      default: return '·';
    }
  })();

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <UserSprite size={11} />
          <span className="gb-pix gb-pix-xs">FICHA</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs">ID {user?.id?.slice(-4).toUpperCase() || '----'}</span>
        </div>
      </div>

      <div className="gb-body" style={{ gap: 5 }}>
        <div className="gb-card">
          <div className="gb-row" style={{ gap: 8 }}>
            <div className="gb-card-avatar" style={{ width: 48, height: 48 }}>
              <UserSprite size={32} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="gb-pix gb-pix-sm"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {(user?.name || 'VECINO').toUpperCase()}
              </div>
              <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, marginTop: 2 }}>
                {user?.email}
              </div>
              <div className="gb-pix gb-pix-xs" style={{ marginTop: 4 }}>
                {roleIcon} {roleLabel(user?.role)}
              </div>
            </div>
          </div>

          <div className="gb-divider" />

          <div className="gb-card-row">
            <span className="gb-card-label">COMUNIDAD</span>
            <span style={{ maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {community?.name || '—'}
            </span>
          </div>
          <div className="gb-card-row">
            <span className="gb-card-label">UNIDAD</span>
            <span>{user?.unit?.code || '—'}</span>
          </div>
          <div className="gb-card-row">
            <span className="gb-card-label">CÓDIGO</span>
            <span style={{ fontFamily: 'var(--mono)' }}>{community?.invite_code || '—'}</span>
          </div>
        </div>

        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, marginTop: 4 }}>
          INSIGNIAS
        </div>
        <div className="gb-row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <Badge icon={<UserSprite size={10} />} label="REGISTRADO" />
          <Badge icon={<MailSprite size={10} />} label="VERIFICADO" />
          {user?.unit?.code && <Badge icon={<KeySprite size={10} />} label={user.unit.code} />}
          {community?.invite_code && <Badge icon={<QRSprite size={10} />} label={community.invite_code} />}
        </div>

        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, marginTop: 6 }}>
          STATS
        </div>
        <div className="gb-row" style={{ gap: 8, justifyContent: 'space-around' }}>
          <Stat icon={<HeartSprite size={14} />} value="100" label="HP" />
          <Stat icon={<CoinSprite size={14} />} value={(community as any)?.balance?.toString() ?? '—'} label="$" />
          <Stat icon={<DoorSprite size={14} />} value={doorsOpened === null ? '—' : doorsOpened.toString()} label="ABRIR" />
          <Stat icon={<BellSprite size={14} />} value={bellRings === null ? '—' : bellRings.toString()} label="TIMBRE" />
        </div>
      </div>
    </div>
  );
}

function Badge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '2px 4px',
        border: '1px solid var(--gb-dark)',
        background: 'var(--gb-bg)',
        fontSize: 5,
      }}
    >
      {icon}
      <span className="gb-pix">{label}</span>
    </div>
  );
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div className="gb-pix gb-pix-xs" style={{ fontSize: 7 }}>{value}</div>
      <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 4 }}>{label}</div>
    </div>
  );
}

function roleLabel(role?: string): string {
  switch (role) {
    case 'admin': return 'PRESIDENTE';
    case 'owner': return 'PROPIETARIO';
    case 'resident': return 'INQUILINO';
    case 'member': return 'VECINO';
    default: return 'VECINO';
  }
}